import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';

import { AiChatButton, AiChatMenuItem } from '../../functions/utils.ai';
import { AIChatboxService } from '../../services/ai-chatbox.service';

@Component({
	selector: 'ai-chat-toolbar',
	template: `
		@if (menuButtons().length) {
			<div class="ai-chat-toolbar-quick-prompts">
				@for (btn of menuButtons(); track btn.id) {
					@if (!btn.hidden?.()) {
						<div class="quick-prompt-group">
							<button
								type="button"
								class="custom-button quick-prompt-btn"
								[attr.data-btn-id]="btn.id"
								[disabled]="loading()"
								[style.opacity]="loading() ? 0.35 : 1"
								(click)="onQuickPromptClick(btn)"
							>
								@if (btn.icon) {
									<span class="ai-chat-toolbar-icon" [innerHTML]="trustHtml(btn.icon)"></span>
								}
								{{ btn.label }}
							</button>
							@if (btn.items?.length && openCategory() === btn.id) {
								<div class="quick-prompt-submenu">
									@for (item of btn.items; track item.label) {
										<button
											type="button"
											class="quick-prompt-submenu-item"
											(click)="onSubItemClick(item)"
										>{{ item.label }}</button>
									}
								</div>
							}
						</div>
					}
				}
			</div>
		}
		<div class="ai-chat-toolbar">
			<div class="ai-chat-toolbar-group start">
				@for (btn of startButtons(); track btn.id) {
					@if (!btn.hidden?.()) {
						<button
							type="button"
							class="custom-button ai-chat-toolbar-btn"
							[attr.data-btn-id]="btn.id"
							[ngbTooltip]="btn.tooltip ? (btn.tooltip | translate) : undefined"
							[disabled]="loading()"
							[style.opacity]="loading() ? 0.35 : 1"
							(click)="btn.onClick()"
						>
							@if (btn.icon) {
								<span class="ai-chat-toolbar-icon" [innerHTML]="trustHtml(btn.icon)"></span>
							}
							@if (btn.label) {
								<span class="ai-chat-toolbar-label">{{ btn.label }}</span>
							}
						</button>
					}
				}
			</div>
			<div class="ai-chat-toolbar-group end">
				@for (btn of endButtons(); track btn.id) {
					@if (!btn.hidden?.()) {
						<button
							type="button"
							class="custom-button ai-chat-toolbar-btn"
							[attr.data-btn-id]="btn.id"
							[ngbTooltip]="btn.tooltip ? (btn.tooltip | translate) : undefined"
							[disabled]="loading()"
							[style.opacity]="loading() ? 0.35 : 1"
							(click)="btn.onClick()"
						>
							@if (btn.icon) {
								<span class="ai-chat-toolbar-icon" [innerHTML]="trustHtml(btn.icon)"></span>
							}
						</button>
					}
				}
			</div>
		</div>
	`,
	styles: `
		:host {
			display: flex;
			flex: 0 0 auto;
			padding: 4px 20px 0;
		}
		.ai-chat-toolbar {
			display: flex;
			justify-content: space-between;
			align-items: center;
			gap: 4px;
			margin-left: auto;
		}
		.ai-chat-toolbar-group {
			display: flex;
			align-items: center;
			gap: 4px;
		}
		.ai-chat-toolbar-btn {
			width: 28px;
			height: 28px;
			border: none;
			border-radius: 50%;
			background: transparent;
			color: #6b7280;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			transition: background-color .15s, opacity .15s;
		}
		.ai-chat-toolbar-btn:hover:not(:disabled) {
			background: rgba(0, 0, 0, .06);
		}
		.ai-chat-toolbar-btn:disabled {
			cursor: default;
		}
		.ai-chat-toolbar-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 16px;
			height: 16px;
		}
		.ai-chat-toolbar-label {
			margin-left: 4px;
			font-size: 0.85em;
		}
		.ai-chat-toolbar-quick-prompts {
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			margin-top: 6px;
		}
		.quick-prompt-btn {
			display: flex;
			align-items: center;
			gap: 6px;
			border: 1px solid rgba(0, 0, 0, .15);
			border-radius: 999px;
			padding: 6px 14px;
			background: transparent;
			color: #374151;
			font-size: 0.85em;
			cursor: pointer;
			transition: background-color .15s, opacity .15s;
		}
		.quick-prompt-btn:hover:not(:disabled) {
			background: rgba(0, 0, 0, .04);
		}
		.quick-prompt-btn:disabled {
			cursor: default;
		}
		.quick-prompt-group {
			position: relative;
		}
		.quick-prompt-submenu {
			position: absolute;
			bottom: 100%;
			left: 0;
			min-width: 240px;
			background: white;
			border: 1px solid #e2e2e2;
			border-radius: 10px;
			box-shadow: 0 6px 18px rgba(0,0,0,.15);
			z-index: 20;
			display: flex;
			flex-direction: column;
			overflow: hidden;
			margin-bottom: 6px;
		}
		.quick-prompt-submenu-item {
			border: none;
			background: transparent;
			text-align: left;
			padding: 8px 12px;
			cursor: pointer;
		}
		.quick-prompt-submenu-item:hover {
			background: rgba(0,0,0,.04);
		}
	`,
	imports: [CommonModule, TranslateModule, NgbTooltipModule],
})
export class AIChatToolbarComponent {
	i18n = inject(TranslateService)
	ais = inject(AIChatboxService)
	sanitizer = inject(DomSanitizer)

	buttons = input<AiChatButton[]>([])

	loading = toSignal(this.ais.loading$, { initialValue: false })

	startButtons = computed(() => this.buttons().filter(b => b.position === 'toolbar-start'))
	endButtons = computed(() => this.buttons().filter(b => b.position === 'toolbar-end'))
	menuButtons = computed(() => this.buttons().filter(b => b.position === 'menu'))

	openCategory = signal<string | null>(null)

	onQuickPromptClick(btn: AiChatButton) {
		if (btn.items?.length) {
			this.openCategory.set(this.openCategory() === btn.id ? null : btn.id)
		} else {
			btn.onClick()
		}
	}

	onSubItemClick(item: AiChatMenuItem) {
		item.onClick()
		this.openCategory.set(null)
	}

	// Angular's default HTML sanitizer allowlist strips <svg>; these icon strings are static
	// constants from utils.ai.ts, never user-provided, so bypassing is safe.
	trustHtml(html?: string) {
		return html ? this.sanitizer.bypassSecurityTrustHtml(html) : '';
	}
}
